import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '@/contexts/AuthContext';
import { hasAiFeatures } from '@/lib/planFeatures';
import {
    fetchGoalsService,
    fetchGoalChecksService,
    updateGoalService,
    createGoalCheckService,
    nextCheckDateAfter,
    Goal,
} from '@/services/goals/goalsService';

const euro = (n: number) => `€ ${Math.round(n).toLocaleString('it-IT')}`;

/**
 * Notifica in dashboard (solo titolare): obiettivi con piano attivo che hanno
 * una verifica periodica in scadenza (es. "rata pagata questo mese?").
 */
const HomeGoalChecks: React.FC = () => {
    const { organization, subscriptionPlan } = useAuth();
    const navigate = useNavigate();
    const [due, setDue] = useState<Goal[]>([]);
    const [checksCount, setChecksCount] = useState<Record<string, number>>({});

    // Gli obiettivi sono una funzionalità del piano AI: sotto, niente fetch né banner.
    const aiEnabled = hasAiFeatures(subscriptionPlan);

    const load = async () => {
        if (!organization?.id || !aiEnabled) return;
        const [goalsRes, checksRes] = await Promise.all([
            fetchGoalsService(Number(organization.id)),
            fetchGoalChecksService(Number(organization.id)),
        ]);
        if (!goalsRes.success) return;
        const today = new Date();
        setDue(
            goalsRes.data.filter(
                (g) => g.plan_accepted_at && g.status !== 'completed' && g.next_check_date && new Date(g.next_check_date) <= today,
            ),
        );
        if (checksRes.success) {
            const counts: Record<string, number> = {};
            checksRes.data.forEach((c) => { counts[c.goal_id] = (counts[c.goal_id] || 0) + 1; });
            setChecksCount(counts);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organization?.id]);

    const confirm = async (g: Goal, status: 'done' | 'missed') => {
        if (!organization?.id || !g.next_check_date) return;
        const res = await createGoalCheckService({
            goal_id: g.id,
            shop_id: Number(organization.id),
            period_end: g.next_check_date,
            status,
        });
        if (!res.success) {
            toast.error('Errore nella conferma');
            return;
        }
        const finished = g.plan_duration_months != null && (checksCount[g.id] || 0) + 1 >= g.plan_duration_months;
        await updateGoalService(g.id, finished
            ? { next_check_date: null, status: 'completed' }
            : { next_check_date: nextCheckDateAfter(g.next_check_date) });
        toast.success(status === 'done' ? 'Verifica confermata' : 'Registrato: periodo non rispettato');
        await load();
    };

    if (!aiEnabled || due.length === 0) return null;

    return (
        <div className="bg-white rounded-3xl border border-amber-200 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center">
                    <Target size={20} />
                </div>
                <div>
                    <h3 className="font-bold text-gray-900 leading-none">Obiettivi da verificare</h3>
                    <p className="text-sm text-gray-400 mt-1">Conferma se hai rispettato l'impegno del periodo</p>
                </div>
            </div>
            <div className="space-y-3">
                {due.map((g) => (
                    <div key={g.id} className="flex items-center justify-between gap-3 rounded-2xl bg-amber-50/60 border border-amber-100 px-4 py-3 flex-wrap">
                        <div className="min-w-0">
                            <p className="font-bold text-gray-900 text-sm truncate">
                                {g.title || (g.type === 'economic' ? 'Obiettivo economico' : 'Obiettivo clienti')}
                            </p>
                            <p className="text-xs text-gray-500">
                                {g.type === 'economic'
                                    ? `Rata ${euro(g.plan_monthly_amount || 0)} · scadenza ${g.next_check_date ? new Date(g.next_check_date).toLocaleDateString('it-IT') : ''}`
                                    : `${g.plan_monthly_amount} clienti nel periodo · scadenza ${g.next_check_date ? new Date(g.next_check_date).toLocaleDateString('it-IT') : ''}`}
                            </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <button onClick={() => confirm(g, 'done')} className="px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-bold transition-colors">
                                ✓ Rispettato
                            </button>
                            <button onClick={() => confirm(g, 'missed')} className="px-3 py-1.5 rounded-lg bg-white border border-red-200 text-red-600 hover:bg-red-50 text-xs font-bold transition-colors">
                                ✗ No
                            </button>
                            <button onClick={() => navigate('/assistant')} title="Vai agli obiettivi" className="p-1.5 text-gray-400 hover:text-gray-700 transition-colors">
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default HomeGoalChecks;
